/**
 * WCC360 v2 — the canonical career list.
 *
 * Rendered instead of the on-device preview once the sealed report exists.
 * Every field here comes straight off the sealed payload: no re-scoring, no
 * re-tiering, no invented salary. If the payload has no explanation for a
 * row, we say nothing rather than write one.
 */
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import type { CanonicalResult } from "../canonical";

const TIER_HEADINGS: Array<{ tier: string; heading: string; subtitle: string }> = [
  { tier: "best", heading: "Best matches", subtitle: "These fit what you told us on every count." },
  { tier: "good", heading: "Also consider", subtitle: "Strong fit, with a gap or two you'd want to know about." },
  { tier: "explore", heading: "Worth exploring", subtitle: "Further from where you are today, and worth a look anyway." },
];

export function CanonicalCareerList({ canonical }: { canonical: CanonicalResult }) {
  if (canonical.careers.length === 0) {
    return (
      <section className="mt-12 rounded-3xl border border-border bg-card p-6">
        <h2 className="font-display text-xl font-medium">Your report is sealed.</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          It doesn't rank job titles for you — and at this stage that's the right call.{" "}
          <Link to="/report" className="text-primary underline underline-offset-2">
            Read what it does say
          </Link>
          .
        </p>
      </section>
    );
  }

  // Display only: top 3 in sealed order, then 3 stretch roles (explore tier first).
  const ranked = TIER_HEADINGS.flatMap(({ tier }) => canonical.careers.filter((c) => c.tier === tier));
  const rest = ranked.slice(3);
  const groups = [
    { key: "top", heading: "Your top 3 matches", subtitle: "Your closest fits today, based on what you told us.", rows: ranked.slice(0, 3) },
    {
      key: "stretch",
      heading: "3 stretch roles",
      subtitle: "A step further from where you are today — worth growing toward.",
      rows: [...rest.filter((c) => c.tier === "explore"), ...rest.filter((c) => c.tier !== "explore")].slice(0, 3),
    },
  ];

  return (
    <>
      {groups.map(({ key: tier, heading, subtitle, rows }) => {
        if (rows.length === 0) return null;
        return (
          <section className="mt-12" key={tier}>
            <h2 className="font-display text-2xl md:text-3xl font-medium">{heading}</h2>
            <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>
            <div className="mt-4 space-y-3">
              {rows.map((c) => (
                <article key={c.careerId} className="rounded-3xl border border-border bg-card p-5 md:p-6">
                  <div className="font-display text-lg md:text-xl font-medium leading-snug">
                    {c.careerName}
                  </div>
                  <div className="text-sm text-muted-foreground">{c.cluster}</div>
                  {c.explanation && (
                    <p className="mt-3 text-sm text-foreground/90 leading-relaxed">
                      {c.explanation}
                    </p>
                  )}
                  <Link
                    to={`/careers/${c.careerId}`}
                    state={{ from: "/assessment/v2/result" }}
                    className="mt-4 inline-flex items-center justify-center gap-2 min-h-[48px] px-5
                      rounded-2xl bg-primary text-primary-foreground font-medium hover:opacity-90
                      focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    Explore this career <ArrowRight className="h-4 w-4" aria-hidden />
                  </Link>
                </article>
              ))}
            </div>
          </section>
        );
      })}

      <p className="mt-10 text-xs text-muted-foreground">
        Sealed report{canonical.reportHash ? ` · ${canonical.reportHash.slice(0, 12)}` : ""} ·
        generated {new Date(canonical.generatedAt).toLocaleString()}
      </p>
    </>
  );
}
